// Phase 21 · Admin rooms moderation queue
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mic, Video, EyeOff, Undo2, Trash2, Flag } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(iso) { try { return new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } }

function RoomRow({ r, onAction }) {
    const isAudio = r.room_type === "audio";
    const act = async (action) => {
        const reason = prompt(`Reason for ${action}?`) || "moderation";
        try {
            const resp = await fetch(`${API}/api/admin/community/rooms/${r.id}/moderate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ action, reason }),
            });
            if (resp.ok) { toast.success(`Room ${action}`); onAction?.(); }
            else toast.error(`${action} failed`);
        } catch { toast.error("Network error"); }
    };
    return (
        <div className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-4 space-y-2" data-testid={`admin-room-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${isAudio ? "bg-[#FFFFFF]/10 text-[#FFFFFF]" : "bg-[#FAFAFA]/10 text-[#FAFAFA]"}`}>
                            {isAudio ? <Mic size={10} /> : <Video size={10} />} {r.room_type}
                        </span>
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#1A1A1A] text-neutral-400 capitalize">
                            {r.status}
                        </span>
                        <span className="text-[10px] text-neutral-500">·</span>
                        <span className="text-[10px] text-neutral-500">created: {fmt(r.created_at)}</span>
                    </div>
                    <Link to={`/rooms/${isAudio ? "audio" : "video"}/${r.slug || r.id}`} className="block mt-1 text-sm font-semibold text-neutral-100 hover:text-[#FFFFFF]">
                        {r.title}
                    </Link>
                    <p className="text-[11px] text-neutral-500 mt-1">host @{r.host_username} · {r.participant_count} participants{isAudio ? ` · ${r.speaker_count}/${r.max_speakers} speakers` : ""}</p>
                </div>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-[#1A1A1A]">
                {r.status !== "hidden" && (
                    <button type="button" onClick={() => act("hide")} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-neutral-700/20 text-neutral-300 border border-neutral-600/50 rounded-lg hover:bg-neutral-700/40" data-testid={`admin-room-hide-${r.id}`}>
                        <EyeOff size={12} /> Hide
                    </button>
                )}
                {r.status === "hidden" && (
                    <button type="button" onClick={() => act("restore")} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#CCCCCC]/10 text-[#CCCCCC] border border-[#CCCCCC]/40 rounded-lg hover:bg-[#CCCCCC]/20" data-testid={`admin-room-restore-${r.id}`}>
                        <Undo2 size={12} /> Restore
                    </button>
                )}
                <button type="button" onClick={() => act("delete")} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#FAFAFA] text-white font-semibold rounded-lg hover:bg-[#FAFAFA]/80" data-testid={`admin-room-delete-${r.id}`}>
                    <Trash2 size={12} /> Delete
                </button>
                <button type="button" onClick={async()=>{const rs=await fetch(`${API}/api/admin/subscriptions/gating/rooms/${r.id}/premium-only`,{method:"PATCH",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify({value:!r.premium_only,reason:"admin toggle"})});if(rs.ok){toast.success(`Premium ${!r.premium_only?"ON":"OFF"}`);onAction?.();}else toast.error("Toggle failed");}} className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg ${r.premium_only?"bg-[#FFFFFF]/20 text-[#FFFFFF] border border-[#FFFFFF]/40":"bg-neutral-700/20 text-neutral-300 border border-neutral-600/50"}`} data-testid={`admin-room-premium-${r.id}`}>Premium: {r.premium_only?"ON":"OFF"}</button>
            </div>
        </div>
    );
}

export default function AdminCommunityRooms() {
    const [items, setItems] = useState([]);
    const [reports, setReports] = useState([]);
    const [status, setStatus] = useState("");
    const [disabled, setDisabled] = useState(false);
    const [tab, setTab] = useState("rooms");

    const load = async () => {
        setDisabled(false);
        try {
            const params = new URLSearchParams({ limit: "50" });
            if (status) params.set("status", status);
            const r = await fetch(`${API}/api/admin/community/rooms?${params}`, { credentials: "include" });
            if (r.status === 503) { setDisabled(true); return; }
            if (r.ok) { const d = await r.json(); setItems(d.items || []); }
            const rp = await fetch(`${API}/api/admin/community/rooms/reports?status=pending&limit=50`, { credentials: "include" });
            if (rp.ok) { const d = await rp.json(); setReports(d.items || []); }
        } catch {}
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter" data-testid="admin-rooms-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to="/admin" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Admin
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <h1 className="text-base font-semibold flex items-center gap-2">
                        <Mic size={16} className="text-[#FFFFFF]" /> Rooms
                    </h1>
                </div>
            </header>
            <main className="max-w-5xl mx-auto px-4 py-6 space-y-4">
                {disabled && (
                    <div className="bg-[#FAFAFA]/10 border border-[#FAFAFA]/30 rounded-lg p-4 text-sm text-[#FAFAFA]">
                        Rooms engine disabled — moderation frozen.
                    </div>
                )}
                <div className="flex items-center gap-2 text-xs">
                    <button type="button" onClick={() => setTab("rooms")} className={`px-3 py-1.5 rounded-full border transition-all ${tab === "rooms" ? "bg-[#FFFFFF]/10 border-[#FFFFFF]/40 text-[#FFFFFF]" : "border-[#1A1A1A] text-neutral-400"}`} data-testid="admin-rooms-tab-all">
                        All rooms ({items.length})
                    </button>
                    <button type="button" onClick={() => setTab("reports")} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border transition-all ${tab === "reports" ? "bg-[#FAFAFA]/10 border-[#FAFAFA]/40 text-[#FAFAFA]" : "border-[#1A1A1A] text-neutral-400"}`} data-testid="admin-rooms-tab-reports">
                        <Flag size={11} /> Pending reports ({reports.length})
                    </button>
                    {tab === "rooms" && (
                        <select value={status} onChange={(e) => setStatus(e.target.value)} className="ml-auto bg-[#111111] border border-[#1A1A1A] rounded-lg px-3 py-1.5 text-xs text-neutral-200" data-testid="admin-rooms-status-filter">
                            <option value="">All statuses</option>
                            <option value="scheduled">scheduled</option>
                            <option value="live">live</option>
                            <option value="ended">ended</option>
                            <option value="hidden">hidden</option>
                            <option value="cancelled">cancelled</option>
                        </select>
                    )}
                </div>
                {tab === "rooms" && (
                    <div className="space-y-3">
                        {!items.length && !disabled && <p className="text-sm text-neutral-500 italic">No rooms</p>}
                        {items.map((r) => <RoomRow key={r.id} r={r} onAction={load} />)}
                    </div>
                )}
                {tab === "reports" && (
                    <div className="space-y-3">
                        {!reports.length && <p className="text-sm text-neutral-500 italic">No pending reports</p>}
                        {reports.map((r) => (
                            <div key={r.id} className="bg-[#111111] border border-[#FAFAFA]/30 rounded-xl p-4">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FAFAFA]/10 text-[#FAFAFA] font-bold">
                                        {r.reason}
                                    </span>
                                    <span className="text-[10px] text-neutral-500">reported {fmt(r.created_at)}</span>
                                </div>
                                {r.notes && <p className="text-xs text-neutral-400 mt-1">Note: {r.notes}</p>}
                                {r.room_snapshot && (
                                    <div className="mt-2 pt-2 border-t border-[#1A1A1A]">
                                        <Link to={`/rooms/${r.room_snapshot.room_type === 'audio' ? 'audio' : 'video'}/${r.room_snapshot.slug || r.room_snapshot.id}`} className="text-sm font-semibold text-neutral-100 hover:text-[#FFFFFF]">
                                            {r.room_snapshot.title}
                                        </Link>
                                        <p className="text-[11px] text-neutral-500">host @{r.room_snapshot.host_username} · status {r.room_snapshot.status} · type {r.room_snapshot.room_type}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
